import { Link } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/Seo";
import { BUSINESS } from "@/lib/constants";
import { breadcrumbSchema, organizationSchema } from "@/lib/schema";
import { Reveal } from "@/components/Reveal";

const FACTS = [
  { label: "Constitution", value: "Sole Proprietorship" },
  { label: "Proprietor", value: BUSINESS.proprietor },
  { label: "Base", value: `${BUSINESS.locality}, ${BUSINESS.region}` },
  { label: "Primary market", value: "Hyderabad & Telangana" },
  { label: "Trade focus", value: "Green cardamom, black pepper, whole spices" },
  { label: "Working hours", value: BUSINESS.hours },
];

const BLOCKS = [
  {
    heading: "Who we are",
    body: [
      `${BUSINESS.name} is a Hyderabad-based wholesale spice enterprise run as a sole proprietorship by ${BUSINESS.proprietor}. We trade green cardamom, black pepper and selected whole spices for business buyers rather than retail consumers.`,
      "Our work is straightforward: identify suitable lots, inspect and segregate them, describe them honestly, and supply exactly what the buyer approved.",
    ],
  },
  {
    heading: "How we trade",
    body: [
      "Spices are agricultural produce, so each lot differs in size, colour and aroma. We therefore trade lot-wise rather than against fixed catalogue specifications. Grades such as 6–7 mm or 8 mm+ cardamom are indicative commercial categories, and the material supplied is always the lot the buyer has seen or approved.",
      "Rates move with the market, so we do not publish prices. Every requirement receives a written quotation that states the specification, quantity and validity period.",
    ],
  },
  {
    heading: "Buyers we work with",
    body: [
      "Spice wholesalers and traders, kirana and grocery retailers, dry-fruit and gourmet stores, sweet shops, bakeries, caterers, tea and masala businesses, food manufacturers, institutional kitchens, hotels and district distributors.",
    ],
  },
  {
    heading: "Quotation and sample approval",
    body: [
      "We share actual-lot photographs or dispatch a physical sample so that the buyer can approve the material before committing. Orders proceed against advance or pre-dispatch payment, and dispatch follows payment clearance.",
      "This keeps expectations clear on both sides and avoids disputes about grade after delivery.",
    ],
  },
  {
    heading: "Where we supply",
    body: [
      "Hyderabad and the rest of Telangana form our primary market. Orders from other parts of India are considered subject to quantity, logistics, payment confirmation and commercial feasibility.",
    ],
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About Nandanaaya Spices | Wholesale Spice Supplier Hyderabad"
        description="Nandanaaya Spices is a Hyderabad-based sole proprietorship trading green cardamom, black pepper and selected whole spices lot-wise for commercial buyers in Telangana."
        path="/about"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [organizationSchema(), breadcrumbSchema([{ name: "About Us", url: "/about" }])],
        }}
      />
      <PageShell
        testid="about-page"
        title="About Nandanaaya Spices"
        subtitle="A Hyderabad-based wholesale spice enterprise trading cardamom, pepper and selected whole spices lot-wise for commercial buyers."
        breadcrumbs={[{ label: "About Us" }]}
      >
        <section className="py-14 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-8">
              {BLOCKS.map((block) => (
                <Reveal key={block.heading} className="space-y-3">
                  <h2 className="text-xl font-bold text-spice-ink">{block.heading}</h2>
                  {block.body.map((para) => (
                    <p key={para.slice(0, 32)} className="text-sm text-spice-muted leading-relaxed">{para}</p>
                  ))}
                </Reveal>
              ))}

              <Reveal className="pt-2">
                <Link
                  to="/contact"
                  data-testid="about-contact-cta"
                  className="inline-flex items-center justify-center px-6 py-3 bg-spice-green text-white text-sm font-semibold rounded-[9px] hover:bg-spice-green-dark transition-colors"
                >
                  Send your requirement
                </Link>
              </Reveal>
            </div>

            <Reveal className="lg:col-span-1">
              <div className="bg-white border border-spice-line rounded-xl p-6 space-y-4 sticky top-28" data-testid="about-facts-card">
                <h2 className="text-xs font-bold uppercase tracking-widest text-spice-green">Business snapshot</h2>
                <dl className="space-y-3 text-xs">
                  {FACTS.map((fact) => (
                    <div key={fact.label}>
                      <dt className="text-spice-subtle">{fact.label}</dt>
                      <dd className="text-sm font-semibold text-spice-ink">{fact.value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="pt-3 border-t border-spice-line space-y-1 text-xs">
                  <div className="text-spice-subtle">Registrations</div>
                  <div className="text-spice-ink">GSTIN: <strong>{BUSINESS.gstin}</strong></div>
                  <div className="text-spice-ink">FSSAI: <strong>{BUSINESS.fssai}</strong></div>
                  <div className="text-spice-ink">Udyam: <strong>{BUSINESS.udyam}</strong></div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </PageShell>
    </>
  );
}
